import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { loginRequest } from "../auth/msalConfig";
import { msalInstance } from "@/services/msalInstance";
import { AuthContext } from "@/context/AuthContext";

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const initializeAuth = async () => {
      try {
        await msalInstance.initialize();
        const accounts = msalInstance.getAllAccounts();

        if (accounts.length > 0) {
          try {
            // Try to silently acquire a token
            await msalInstance.acquireTokenSilent({
              ...loginRequest,
              account: accounts[0],
            });
            setIsAuthenticated(true);
          } catch (error) {
            console.log("Silent token acquisition failed:", error);
            setIsAuthenticated(false);
          }
        } else {
          setIsAuthenticated(false);
        }
      } catch (error) {
        console.error("Failed to initialize MSAL:", error);
      } finally {
        setIsInitialized(true);
        setIsLoading(false);
      }
    };

    initializeAuth();
  }, []);

  const login = async () => {
    try {
      setIsLoading(true);
      await msalInstance.loginPopup(loginRequest);
      setIsAuthenticated(true);
      setIsLoading(false);
    } catch (error) {
      console.error("Login failed:", error);
      setIsAuthenticated(false);
      setIsLoading(false);
    }
  };

  // Handle redirect response
  useEffect(() => {
    const handleRedirectResponse = async () => {
      if (!isInitialized) return;

      try {
        setIsLoading(true);
        const response = await msalInstance.handleRedirectPromise();
        if (response) {
          setIsAuthenticated(true);
          navigate(location.pathname === "/login" ? "/" : location.pathname);
        }
      } catch (error) {
        console.error("Error handling redirect:", error);
        setIsAuthenticated(false);
      } finally {
        setIsLoading(false);
      }
    };

    handleRedirectResponse();
  }, [navigate, location.pathname, isInitialized]);

  const logout = () => {
    msalInstance.logoutRedirect();
    setIsAuthenticated(false);
  };

  return (
    <AuthContext.Provider value={{ isAuthenticated, isInitialized, isLoading, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};
